/**
 * EWSCustomersTable - Paginated customer table
 *
 * Displays customer records with consent ID, balance, risk metrics,
 * EMI count, and SIPs. Supports pagination, sorting, and row click for drawer opening.
 */

import React, { useMemo } from 'react';
import { Table, type TableColumn, type TableSortState, Tag } from 'equal-ds-ui';
import { PAGE_SIZE_OPTIONS } from '../../../constants/pagination.constants';
import {
  EWSCustomersTableProps,
  EWSCustomer,
  formatINR,
  formatIndianNumber,
  RiskLevel,
  getRiskLevelColor,
  CustomerSortColumn,
} from '../../../types/ews.types';

/**
 * Truncate consent ID for display with copy functionality.
 */
const ConsentIdCell: React.FC<{ consentId: string | undefined }> = ({ consentId }) => {
  const [copied, setCopied] = React.useState(false);

  // Handle undefined or non-string values
  const displayId = consentId && typeof consentId === 'string' ? consentId : '';

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!displayId) return;
    try {
      await navigator.clipboard.writeText(displayId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!displayId) {
    return <span className="text-gray-400">—</span>;
  }

  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-sm truncate max-w-[180px]" title={displayId}>
        {displayId.length > 20 ? `${displayId.substring(0, 20)}...` : displayId}
      </span>
      <button
        onClick={handleCopy}
        className="p-1 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
        title="Copy to clipboard"
      >
        {copied ? '✓' : '⧉'}
      </button>
    </div>
  );
};

/**
 * Risk level chip component using Tag from equal-ds.
 */
const RiskLevelChip: React.FC<{ riskLevel: RiskLevel }> = ({ riskLevel }) => {
  const colors = getRiskLevelColor(riskLevel);

  return (
    <Tag
      size="sm"
      className={`${colors.bg} ${colors.text} ${colors.border} border`}
    >
      {riskLevel}
    </Tag>
  );
};

export const EWSCustomersTable: React.FC<EWSCustomersTableProps> = ({
  customers,
  isLoading,
  pagination,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  sortBy,
  sortOrder,
  onSortChange,
}) => {
  // Convert parent's sortBy/sortOrder to TableSortState format for the Table component
  const sortState: TableSortState | null = useMemo(() => {
    if (!sortBy) return null;
    return {
      column: sortBy,
      direction: sortOrder,
    };
  }, [sortBy, sortOrder]);

  // Handle sort changes from table and call parent's onSortChange
  const handleSort = (newSortState: TableSortState | null) => {
    if (newSortState) {
      onSortChange(newSortState.column as CustomerSortColumn, newSortState.direction);
    } else {
      onSortChange(null, 'desc');
    }
  };

  // Define table columns using accessor pattern (like ConsentTable)
  const columns: TableColumn<EWSCustomer>[] = [
    {
      key: 'trackingId',
      header: 'CONSENT ID',
      width: 240,
      locked: true,
      render: (_value, row) => (
        <ConsentIdCell consentId={row.trackingId || row.consentId} />
      ),
    },
    {
      key: 'currentBalance',
      header: 'CURRENT BALANCE',
      width: 160,
      sortable: true,
      accessor: (row) => formatINR(row.currentBalance),
    },
    {
      key: 'riskLevel',
      header: 'RISK LEVEL',
      width: 140,
      sortable: true,
      render: (_value, row) => <RiskLevelChip riskLevel={row.riskLevel ?? 'LOW'} />,
    },
    {
      key: 'odwMaxBalanceWindow',
      header: 'OPTIMUM WINDOW TO TRIGGER',
      width: 220,
      accessor: (row) => {
        const window = row.odwMaxBalanceWindow?.[0];
        return window ? `Day ${window.startDay}-${window.endDay}` : '-';
      },
    },
    {
      key: 'riskScore',
      header: 'RISK SCORE',
      width: 120,
      sortable: true,
      accessor: (row) => `${row.riskScore ?? 0}/40`,
    },
    {
      key: 'foir',
      header: 'FOIR',
      width: 100,
      sortable: true,
      accessor: (row) => row.foir ? row.foir.toFixed(2) : '-',
    },
    {
      key: 'activeEMIsCount',
      header: 'ACTIVE EMIs COUNT',
      width: 160,
      sortable: true,
      accessor: (row) => formatIndianNumber(row.activeEMIsCount),
    },
    {
      key: 'activeSIPs',
      header: 'ACTIVE SIPs',
      width: 120,
      sortable: true,
      accessor: (row) => formatIndianNumber(row.activeSIPs),
    },
  ];

  // Handle row click
  const handleRowClick = (row: EWSCustomer) => {
    onRowClick(row);
  };

  return (
    <Table<EWSCustomer>
      className="flex-1 min-h-0 shadow-sm rounded-xl"
      data={customers}
      columns={columns}
      onRowClick={handleRowClick}
      sortState={sortState}
      onSort={handleSort}
      resizableColumns
      pagination={{
        currentPage: pagination.currentPage,
        pageSize: pagination.pageSize,
        totalItems: pagination.totalRecords,
        onPageChange,
        onPageSizeChange,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
        showPageSizeSelector: true,
        isFetching: isLoading,
      }}
    />
  );
};

export default EWSCustomersTable;
